/**
 * Shared browser-side PDF client for the legacy buttons and React footer.
 * Composite PDFs run as background jobs and are polled until ready; single-page
 * PDFs keep the direct response path to avoid task and polling overhead. The
 * returned promise stays pending through generation so callers can keep their
 * existing spinners active. Requests are time-limited, and transient polling
 * failures are retried without submitting another generation job.
 */
const PDF_POLL_INTERVAL_MS = 3000;
const PDF_MAX_WAIT_MS = 10 * 60 * 1000;
const PDF_REQUEST_TIMEOUT_MS = 30000;
const PDF_MAX_POLL_FAILURES = 3;
const PDF_RETRYABLE_STATUS_CODES = [408, 429, 502, 503, 504];

export type PdfDownloadResponse = {
  content: { content: string };
};

type PdfJobResponse = {
  content?: { content: string };
  jobId?: string;
  status?: 'pending' | 'running' | 'ready' | 'failed';
};

class PdfRequestError extends Error {
  readonly retryable: boolean;

  constructor(status: number) {
    super(`PDF request failed (${status})`);
    this.retryable = PDF_RETRYABLE_STATUS_CODES.indexOf(status) !== -1;
  }
}

export async function requestPdf(
  serviceUrl: string,
  url: string,
  values?: string
): Promise<PdfDownloadResponse> {
  const deadline = Date.now() + PDF_MAX_WAIT_MS;
  let isCompositePdf = false;
  if (values) {
    try {
      const parsedValues = JSON.parse(values) as { pdfTemplateMode?: string };
      isCompositePdf = parsedValues.pdfTemplateMode === 'composite';
    } catch {
      isCompositePdf = false;
    }
  }

  const fetchResponse = async(requestUrl: string, options?: RequestInit): Promise<PdfJobResponse> => {
    const controller = new AbortController();
    const requestTimeout = isCompositePdf ? PDF_REQUEST_TIMEOUT_MS : PDF_MAX_WAIT_MS;
    const timeout = setTimeout(() => controller.abort(), Math.min(requestTimeout, deadline - Date.now()));
    try {
      const response = await fetch(requestUrl, {
        ...options,
        signal: controller.signal,
        credentials: 'same-origin',
        cache: 'no-store'
      });
      if (!response.ok) throw new PdfRequestError(response.status);
      return await response.json() as PdfJobResponse;
    } finally {
      clearTimeout(timeout);
    }
  };

  const result = await fetchResponse(serviceUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, values, async: isCompositePdf })
  });
  const jobId = result.jobId;

  const waitForPdf = async(job: PdfJobResponse, consecutiveFailures: number): Promise<PdfDownloadResponse> => {
    if (job.status !== 'pending' && job.status !== 'running') {
      if (!job.content?.content) throw new Error('PDF generation failed');
      return { content: job.content };
    }
    if (!jobId) throw new Error('PDF service did not return a job ID');
    await new Promise<void>((resolve) => {
      setTimeout(resolve, PDF_POLL_INTERVAL_MS);
    });
    if (Date.now() >= deadline) throw new Error('PDF generation timed out');
    const statusUrl = new URL(serviceUrl, url);
    statusUrl.searchParams.set('jobId', jobId);
    let nextResult: PdfJobResponse;
    try {
      nextResult = await fetchResponse(statusUrl.href);
    } catch (error) {
      const failures = consecutiveFailures + 1;
      if ((error instanceof PdfRequestError && !error.retryable)
        || failures >= PDF_MAX_POLL_FAILURES) throw error;
      return waitForPdf(job, failures);
    }
    return waitForPdf(nextResult, 0);
  };

  return waitForPdf(result, 0);
}
